/**
 * DecisionHistoryDrawer — slide-out panel showing platform_gate_decisions
 * with filters (tenant, capability, gate names multi-select, status,
 * time range) and a paginated table.
 *
 * WQ-235 (Phase 4.9 Track A α9). Operator surface for ad-hoc audit
 * reconstruction. Activated by the AllocationDetailDrawer "View Decision
 * History" button (which pre-filters tenant + capability_type) and
 * usable standalone with the filter dropdowns visible.
 *
 * Pattern reuse:
 *   - Drawer chrome from AllocationDetailDrawer (WQ-230)
 *   - Browser shape from LifecycleTimeline (WQ-244)
 *   - 30s polling from AllocationsTab (WQ-230)
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  countDecisions as defaultCountDecisions,
  listDecisions as defaultListDecisions,
  computeTimeRange,
  type DecisionHistoryFilters,
  type GateDecision,
  type TimeRangePreset,
} from '../../services/decisionHistoryService';

const POLL_INTERVAL_MS = 30_000;
const PAGE_SIZE = 50;

const KNOWN_GATE_NAMES: string[] = [
  'license_compliance',
  'hallucination_policy',
  'kill_switch',
  'kill_switch_capability_halt',
  'kill_switch_capability_restore',
  'sqa_confidence_floor',
  'compute_tier',
  'dataset_composition_policy',
];

const KNOWN_CAPABILITY_TYPES: string[] = [
  'prediction',
  'automl',
  'monte_carlo',
  'data_quality',
  'fairness',
  'explainability',
  'dataset_composition',
  'experiment',
  'algorithm',
  'connector',
  'skill',
];

export interface DecisionHistoryReadService {
  listDecisions: typeof defaultListDecisions;
  countDecisions: typeof defaultCountDecisions;
}

interface DecisionHistoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Pre-filter mode: opened from AllocationDetailDrawer */
  preFilteredTenantId?: string;
  preFilteredCapabilityType?: string;
  /** Optional override for tests */
  serviceOverride?: DecisionHistoryReadService;
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls =
    status === 'approved'
      ? 'badge badge-success'
      : status === 'denied'
      ? 'badge badge-danger'
      : status === 'escalated'
      ? 'badge badge-warning'
      : 'badge badge-info';
  return <span className={cls} data-testid={`decision-status-${status}`}>{status}</span>;
};

const formatTs = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString() : '—';

const truncate = (s: string | null, n = 60): string => {
  if (!s) return '—';
  return s.length > n ? `${s.slice(0, n)}…` : s;
};

const DecisionHistoryDrawer: React.FC<DecisionHistoryDrawerProps> = ({
  isOpen,
  onClose,
  preFilteredTenantId,
  preFilteredCapabilityType,
  serviceOverride,
}) => {
  const { authFetch } = useAuth();
  const svc = serviceOverride ?? {
    listDecisions: defaultListDecisions,
    countDecisions: defaultCountDecisions,
  };

  const [tenantFilter, setTenantFilter] = useState<string>(preFilteredTenantId ?? '');
  const [capabilityFilter, setCapabilityFilter] = useState<string>(
    preFilteredCapabilityType ?? '',
  );
  const [gateNamesFilter, setGateNamesFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [timeRange, setTimeRange] = useState<TimeRangePreset>('24h');

  const [decisions, setDecisions] = useState<GateDecision[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filters: DecisionHistoryFilters = useMemo(() => {
    const range = computeTimeRange(timeRange);
    return {
      tenant_id: tenantFilter || undefined,
      capability_type: capabilityFilter || undefined,
      gate_names: gateNamesFilter.length > 0 ? gateNamesFilter : undefined,
      decision: statusFilter || undefined,
      start_time: range.start_time,
      end_time: range.end_time,
    };
  }, [tenantFilter, capabilityFilter, gateNamesFilter, statusFilter, timeRange]);

  const reload = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    setError(null);
    try {
      const offset = (page - 1) * PAGE_SIZE;
      const [rows, count] = await Promise.all([
        svc.listDecisions(authFetch, filters, PAGE_SIZE, offset),
        svc.countDecisions(authFetch, filters),
      ]);
      setDecisions(rows);
      setTotal(count);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load decisions';
      setError(msg);
    } finally {
      setLoading(false);
    }
    // svc is stable per drawer instance; dropping it from deps avoids re-poll loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authFetch, filters, page, isOpen]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!isOpen) return;
    const id = window.setInterval(() => void reload(), POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [isOpen, reload]);

  // Reset to page 1 when any filter changes.
  useEffect(() => {
    setPage(1);
  }, [tenantFilter, capabilityFilter, gateNamesFilter, statusFilter, timeRange]);

  if (!isOpen) return null;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const toggleGateName = (name: string) => {
    setGateNamesFilter((prev) =>
      prev.includes(name) ? prev.filter((g) => g !== name) : [...prev, name],
    );
  };

  return (
    <div
      className="drawer-overlay"
      onClick={onClose}
      data-testid="decision-history-drawer"
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 1100,
      }}
    >
      <div
        className="drawer-panel"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: '720px',
          maxWidth: '95vw',
          background: 'var(--background, #fff)',
          boxShadow: '-4px 0 16px rgba(0,0,0,0.2)',
          overflow: 'auto',
          padding: '20px 24px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <h3 style={{ margin: 0 }}>Decision History</h3>
          <button className="btn btn-ghost" onClick={onClose} aria-label="Close">
            {'\u00d7'}
          </button>
        </div>

        {/* Pre-filter badges */}
        {(preFilteredTenantId || preFilteredCapabilityType) && (
          <div
            data-testid="prefilter-badges"
            style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}
          >
            {preFilteredTenantId && (
              <span className="badge badge-info" data-testid="prefilter-tenant">
                Tenant: {preFilteredTenantId.slice(0, 8)}…
              </span>
            )}
            {preFilteredCapabilityType && (
              <span className="badge badge-info" data-testid="prefilter-capability">
                Capability: {preFilteredCapabilityType}
              </span>
            )}
          </div>
        )}

        {/* Filter row */}
        <section
          data-testid="decision-filters"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '8px',
            marginBottom: '16px',
            padding: '12px',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: '4px',
          }}
        >
          {!preFilteredTenantId && (
            <label style={{ display: 'flex', flexDirection: 'column', fontSize: '12px' }}>
              Tenant ID
              <input
                type="text"
                value={tenantFilter}
                onChange={(e) => setTenantFilter(e.target.value)}
                placeholder="UUID (leave blank for all)"
                data-testid="filter-tenant"
                style={{ padding: '4px 6px' }}
              />
            </label>
          )}
          {!preFilteredCapabilityType && (
            <label style={{ display: 'flex', flexDirection: 'column', fontSize: '12px' }}>
              Capability
              <select
                value={capabilityFilter}
                onChange={(e) => setCapabilityFilter(e.target.value)}
                data-testid="filter-capability"
                style={{ padding: '4px 6px' }}
              >
                <option value="">All capabilities</option>
                {KNOWN_CAPABILITY_TYPES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: '12px' }}>
            Status
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="filter-status"
              style={{ padding: '4px 6px' }}
            >
              <option value="">All</option>
              <option value="approved">Approved</option>
              <option value="denied">Denied</option>
              <option value="escalated">Escalated</option>
              <option value="pending">Pending</option>
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: '12px' }}>
            Time range
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as TimeRangePreset)}
              data-testid="filter-time-range"
              style={{ padding: '4px 6px' }}
            >
              <option value="1h">Last 1h</option>
              <option value="24h">Last 24h</option>
              <option value="7d">Last 7d</option>
              <option value="30d">Last 30d</option>
              <option value="custom">All</option>
            </select>
          </label>
          <div style={{ gridColumn: '1 / -1', fontSize: '12px' }}>
            <div style={{ marginBottom: '4px' }}>Gate names</div>
            <div
              data-testid="filter-gate-names"
              style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}
            >
              {KNOWN_GATE_NAMES.map((g) => {
                const selected = gateNamesFilter.includes(g);
                return (
                  <button
                    key={g}
                    type="button"
                    className={selected ? 'btn btn-primary' : 'btn btn-ghost'}
                    data-testid={`gate-name-${g}`}
                    onClick={() => toggleGateName(g)}
                    style={{ fontSize: '11px', padding: '2px 6px' }}
                  >
                    {g}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Table */}
        {loading && (
          <div data-testid="decision-loading" style={{ padding: '12px', textAlign: 'center' }}>
            Loading decisions…
          </div>
        )}
        {error && (
          <div
            data-testid="decision-error"
            className="alert alert-danger"
            style={{ marginBottom: '12px' }}
          >
            {error}
          </div>
        )}
        {!loading && !error && decisions.length === 0 && (
          <div
            data-testid="decision-empty"
            style={{
              padding: '24px',
              textAlign: 'center',
              color: 'var(--text-secondary)',
            }}
          >
            No gate decisions match the current filters.
          </div>
        )}
        {!loading && !error && decisions.length > 0 && (
          <table
            data-testid="decision-table"
            style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}
          >
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                <th style={{ padding: '6px 4px' }}>Time</th>
                <th style={{ padding: '6px 4px' }}>Gate</th>
                <th style={{ padding: '6px 4px' }}>Status</th>
                <th style={{ padding: '6px 4px' }}>Actor</th>
                <th style={{ padding: '6px 4px' }}>Tenant</th>
                <th style={{ padding: '6px 4px' }}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {decisions.map((d) => (
                <React.Fragment key={d.id}>
                  <tr
                    data-testid={`decision-row-${d.id}`}
                    onClick={() => setExpandedId(expandedId === d.id ? null : d.id)}
                    style={{
                      cursor: 'pointer',
                      borderBottom: '1px solid rgba(0,0,0,0.05)',
                    }}
                  >
                    <td style={{ padding: '6px 4px' }}>{formatTs(d.decided_at)}</td>
                    <td style={{ padding: '6px 4px' }}>{d.gate_name}</td>
                    <td style={{ padding: '6px 4px' }}>
                      <StatusBadge status={d.decision} />
                    </td>
                    <td style={{ padding: '6px 4px' }}>{d.actor_id ?? d.actor_type}</td>
                    <td style={{ padding: '6px 4px' }}>
                      {d.tenant_id ? `${d.tenant_id.slice(0, 8)}…` : '—'}
                    </td>
                    <td style={{ padding: '6px 4px' }}>{truncate(d.rationale_text, 50)}</td>
                  </tr>
                  {expandedId === d.id && (
                    <tr data-testid={`decision-row-expanded-${d.id}`}>
                      <td colSpan={6} style={{ padding: '8px', background: 'rgba(0,0,0,0.03)' }}>
                        <pre
                          style={{
                            margin: 0,
                            fontSize: '11px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                          }}
                        >
                          {JSON.stringify(d.decision_metadata ?? {}, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {!loading && !error && total > 0 && (
          <div
            data-testid="decision-pagination"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '12px',
              fontSize: '12px',
            }}
          >
            <span>
              Showing {(page - 1) * PAGE_SIZE + 1}–
              {Math.min(page * PAGE_SIZE, total)} of {total}
            </span>
            <div>
              <button
                className="btn btn-ghost"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="page-prev"
              >
                Previous
              </button>
              <button
                className="btn btn-ghost"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                data-testid="page-next"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DecisionHistoryDrawer;
